import { useIsWeb } from '@/hooks/useIsWeb';
import { bottomTabBarHeight } from '@components/NavBar/BottomNavBar/bottomNavBar.constants';
import NavigationHeader from '@components/NavBar/BottomNavBar/components/NavigationHeader';
import { TopNavBar } from '@components/NavBar/TopNavBar/TopNavBar';
import { OfflineModeBanner } from '@components/OfflineMode/OfflineModeBanner';
import { showToast } from '@components/Toast/toast.service';
import {
  getTabItems,
  notificationsTab,
  userManagementTab,
} from '@components/NavBar/navBar.constants';
import { isTabName, TabName } from '@components/NavBar/navBar.types';
import { Ionicons } from '@expo/vector-icons';
import { useAppTranslation } from '@hooks/useAppTranslation';
import { useAuth } from '@hooks/useAuth';
import { useBreakpoint } from '@hooks/useBreakpoint';
import { useOfflineDegradedMode } from '@hooks/useOfflineDegradedMode';
import { usePageHorizontalPadding } from '@hooks/usePagePadding';
import { canAccessTab } from '@utils/userPermissions';
import { Tabs, useRouter, useSegments } from 'expo-router';
import React, { useEffect } from 'react';
import {
  getFontSize,
  getConfig as getUIConfig,
  getVariableValue,
  useTheme,
} from 'tamagui';
import { NotificationHeader } from '@components/NavBar/NotificationHeader/NotificationHeader';

/**
 * Unified layout component for the tabs section of the app.
 * Always uses Expo Router Tabs to preserve screen state across layout changes.
 * On small screens: shows native bottom tab bar.
 * On large screens: hides native tab bar and overlays custom TopNavBar.
 */
export default function TabsLayout(): React.ReactNode {
  const router = useRouter();
  const { t } = useAppTranslation();
  const { isSmallScreen } = useBreakpoint();
  const isWeb = useIsWeb();
  const fontSize = getFontSize('$3');
  const theme = useTheme();
  const uiConfig = getUIConfig();
  const font = getVariableValue(uiConfig.fonts.body.family);
  const horizontalPadding = usePageHorizontalPadding();
  const tabs = getTabItems({ isWeb, isSmallScreen });
  const { user } = useAuth();
  const { isOfflineDegradedMode } = useOfflineDegradedMode();
  const canSeeUserManagementTab = canAccessTab(userManagementTab, user);
  const shouldHideUserManagementTab = isSmallScreen || !canSeeUserManagementTab;

  const segments = useSegments();
  const currentTab = segments.find(isTabName);
  const routeSegments = segments.filter((segment) => !segment.startsWith('('));
  const isTabRootRoute = routeSegments.length === 1;
  const isInNestedNotificationsRoute =
    routeSegments[routeSegments.length - 1] === TabName.notifications;
  const isInReportCreationFlow = routeSegments.includes(TabName.reportCreation);

  useEffect(() => {
    if (!isOfflineDegradedMode || isInReportCreationFlow) {
      return;
    }

    router.replace('/(tabs)/report-creation');
  }, [isInReportCreationFlow, isOfflineDegradedMode, router]);

  const handleNotificationPress = (): void => {
    if (isOfflineDegradedMode) {
      showToast({
        type: 'error',
        title: t('offlineMode.banner'),
      });
      return;
    }

    if (isSmallScreen) {
      if (isInNestedNotificationsRoute) {
        return;
      }

      if (
        currentTab &&
        currentTab !== TabName.notifications &&
        currentTab !== TabName.reportCreation
      ) {
        router.push(`/(tabs)/${currentTab}/notifications`);
        return;
      }
    }

    if (currentTab !== TabName.notifications) {
      router.push('/(tabs)/notifications');
    }
  };

  const preventTabPressIfOffline = (
    event: { preventDefault: () => void },
    tabName: TabName,
  ): void => {
    if (!isOfflineDegradedMode || tabName === TabName.reportCreation) {
      return;
    }

    event.preventDefault();
    showToast({
      type: 'error',
      title: t('offlineMode.banner'),
    });
  };

  const tabScreens = tabs
    .filter(
      (item) =>
        !shouldHideUserManagementTab || item.name !== TabName.userManagement,
    )
    .map((item) => {
      const isAccessible = canAccessTab(item, user);

      return (
        <Tabs.Screen
          key={item.name}
          name={item.name}
          listeners={{
            tabPress: (event) => {
              preventTabPressIfOffline(event, item.name);
            },
          }}
          options={{
            title: t(item.labelKey),
            // Hide the route entirely for roles that cannot access it
            href: isAccessible ? undefined : null,
            headerShown:
              isAccessible &&
              isSmallScreen &&
              item.name !== TabName.reportCreation &&
              isTabRootRoute,
            tabBarItemStyle: isAccessible ? undefined : { display: 'none' },
            tabBarIcon: ({ color, size }) => (
              <Ionicons name={item.iconName} size={size} color={color} />
            ),
          }}
        />
      );
    });

  if (shouldHideUserManagementTab) {
    tabScreens.push(
      <Tabs.Screen
        key={TabName.userManagement}
        name={TabName.userManagement}
        listeners={{
          tabPress: (event) => {
            preventTabPressIfOffline(event, TabName.userManagement);
          },
        }}
        options={{
          title: t(userManagementTab.labelKey),
          href: null,
          headerShown:
            canSeeUserManagementTab && isSmallScreen && isTabRootRoute,
          tabBarItemStyle: { display: 'none' },
          tabBarIcon: ({ color, size }) => (
            <Ionicons
              name={userManagementTab.iconName}
              size={size}
              color={color}
            />
          ),
        }}
      />,
    );
  }

  tabScreens.push(
    <Tabs.Screen
      key={TabName.notifications}
      name={TabName.notifications}
      listeners={{
        tabPress: (event) => {
          preventTabPressIfOffline(event, TabName.notifications);
        },
      }}
      options={{
        title: t(notificationsTab.labelKey),
        href: isSmallScreen ? null : undefined,
        headerShown: isSmallScreen && isTabRootRoute,
        tabBarItemStyle: isSmallScreen ? { display: 'none' } : undefined,
        tabBarIcon: ({ color, size }) => (
          <Ionicons
            name={notificationsTab.iconName}
            size={size}
            color={color}
          />
        ),
      }}
    />,
  );

  return (
    <>
      {isOfflineDegradedMode && <OfflineModeBanner />}

      {/* Desktop TopNavBar Overlay - only rendered on large screens */}
      {!isSmallScreen && <TopNavBar horizontalMargin={horizontalPadding} />}

      {/* Unified Tabs Navigation - always rendered to maintain screen state */}
      <Tabs
        screenOptions={{
          tabBarActiveTintColor: theme.secondary?.val,
          tabBarInactiveTintColor: theme.secondary50?.val,
          tabBarStyle: isSmallScreen
            ? {
                // Mobile: Show native tab bar
                backgroundColor: theme.background.val,
                borderTopColor: theme.borderColor.val,
                borderTopWidth: 1,
                ...(isWeb ? { height: bottomTabBarHeight } : {}),
              }
            : {
                // Desktop: Hide native tab bar
                display: 'none',
              },
          tabBarLabelStyle: {
            fontSize: fontSize,
            fontFamily: font,
          },
          sceneStyle: {
            backgroundColor: theme.background.val,
          },
          headerStyle: {
            backgroundColor: theme.brand?.val,
          },
          headerTitle: '',
          headerTintColor: theme.background.val,
          headerLeft: () => <NavigationHeader />,
          headerRight: () => (
            <NotificationHeader handlePress={handleNotificationPress} />
          ),
          // Lazy load tabs to preserve state and prevent unnecessary remounts
          lazy: true,
        }}
      >
        {tabScreens}
      </Tabs>
    </>
  );
}
